export default class VideoHero {
  constructor() {
    this.CLIP_RADIUS = 92 / 2;
    this.TRANSITION_DURATION = 800;
    this.MOBILE_BREAKPOINT = 768;
    this.MAX_RETRIES = 5;
    this.RETRY_DELAY = 100;

    this.rafId = null;
    this.isInitialized = false;

    this.initializeWithRetry();
  }

  initializeWithRetry(retryCount = 0) {
    this.elements = {
      clipBtn: document.getElementById('video-hero-clip-btn'),
      bgVideo: document.getElementById('video-hero-bg'),
      maskVideo: document.getElementById('video-hero-mask'),
      label: document.getElementById('video-hero-label'),
      container: document.querySelector('.hero-video__media'),
      heroOverlay: document.getElementById('hero-overlay'),
    };

    const requiredElements = [
      'clipBtn',
      'bgVideo',
      'maskVideo',
      'label',
      'container',
    ];
    const missingElements = requiredElements.filter(
      (key) => !this.elements[key]
    );

    if (missingElements.length > 0) {
      if (retryCount < this.MAX_RETRIES) {
        setTimeout(() => {
          this.initializeWithRetry(retryCount + 1);
        }, this.RETRY_DELAY);
        return;
      } else {
        console.warn(
          'VideoHero: Required elements not found after maximum retries:',
          missingElements
        );
        return;
      }
    }

    this.state = {
      isRevealed: false,
      mouseX: 0,
      mouseY: 0,
      isMouseInside: false,
      hasInitialHover: false,
    };

    // Initialize immediately without waiting for videos
    this.init();
  }

  isMobile() {
    return window.innerWidth < this.MOBILE_BREAKPOINT;
  }

  hasFinePointer() {
    return window.matchMedia('(pointer: fine)').matches;
  }

  shouldShowCursor() {
    return !this.isMobile() && this.hasFinePointer();
  }

  init() {
    this.initializeComponent();
    this.bindEvents();
    this.startVideos();
    this.moveSearchForm();
    this.isInitialized = true;
  }

  startVideos() {
    if (this.elements.bgVideo) {
      this.elements.bgVideo.play().catch(() => {});
    }
    if (this.elements.maskVideo) {
      this.elements.maskVideo.play().catch(() => {});
    }
  }

  initializeComponent() {
    if (this.shouldShowCursor()) {
      this.updateClipPath(0, 0, 0);
      this.elements.label.style.opacity = '0';
      this.elements.label.style.transform = 'translate(-50%, -50%)';
      this.checkInitialMousePosition();
    } else {
      const center = this.getCenterPosition();
      this.updateClipPath(this.CLIP_RADIUS, center.x, center.y);
      this.updateLabelPosition(center.x, center.y);
      this.elements.label.style.opacity = '1';
    }

    if (this.elements.heroOverlay) {
      this.elements.heroOverlay.style.opacity = '1';
    }
  }

  getCenterPosition() {
    const rect = this.elements.container.getBoundingClientRect();
    return {
      x: rect.width / 2,
      y: rect.height / 2,
    };
  }

  checkInitialMousePosition() {
    if (!this.shouldShowCursor()) return;

    requestAnimationFrame(() => {
      const rect = this.elements.container.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const elementAtCenter = document.elementFromPoint(centerX, centerY);
      const isHovering =
        elementAtCenter &&
        (elementAtCenter === this.elements.container ||
          this.elements.container.contains(elementAtCenter));

      if (isHovering) {
        document.addEventListener(
          'mousemove',
          this.handleInitialMouse.bind(this),
          { once: true }
        );
      }
    });
  }

  handleInitialMouse(e) {
    if (!this.shouldShowCursor() || this.state.hasInitialHover) return;

    const rect = this.elements.container.getBoundingClientRect();
    const isInside =
      e.clientX >= rect.left &&
      e.clientX <= rect.right &&
      e.clientY >= rect.top &&
      e.clientY <= rect.bottom;

    if (isInside) {
      this.state.hasInitialHover = true;
      this.state.isMouseInside = true;
      this.state.mouseX = e.clientX - rect.left;
      this.state.mouseY = e.clientY - rect.top;

      this.showCursor();
    }
  }

  updateClipPath(radius, x, y) {
    this.elements.clipBtn.style.clipPath = `circle(${radius}px at ${x}px ${y}px)`;
  }

  updateLabelPosition(x, y) {
    this.elements.label.style.left = `${x}px`;
    this.elements.label.style.top = `${y}px`;
  }

  showCursor() {
    if (!this.shouldShowCursor() || this.state.isRevealed) return;

    this.updateClipPath(this.CLIP_RADIUS, this.state.mouseX, this.state.mouseY);
    this.updateLabelPosition(this.state.mouseX, this.state.mouseY);
    this.elements.label.style.opacity = '1';
  }

  hideCursor() {
    if (!this.shouldShowCursor() || this.state.isRevealed) return;

    this.updateClipPath(0, this.state.mouseX, this.state.mouseY);
    this.elements.label.style.opacity = '0';
  }

  bindEvents() {
    this.elements.bgVideo.addEventListener('play', () => {
      if (this.elements.maskVideo.paused) {
        this.elements.maskVideo.play().catch(() => {});
      }
    });

    this.elements.bgVideo.addEventListener('pause', () => {
      this.elements.maskVideo.pause();
    });

    // Initialize mouse events based on current state
    this.updateMouseEvents();

    this.elements.clipBtn.addEventListener(
      'click',
      this.handleClick.bind(this)
    );

    this.elements.clipBtn.addEventListener('touchend', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.handleClick(e);
    });

    window.addEventListener('resize', this.handleResize.bind(this));
  }

  updateMouseEvents() {
    // Remove existing mouse events
    this.removeMouseEvents();

    // Add mouse events if we should show cursor
    if (this.shouldShowCursor()) {
      this.addMouseEvents();
    }
  }

  addMouseEvents() {
    if (this.mouseMoveHandler) return; // Already added

    this.mouseMoveHandler = this.handleMouseMove.bind(this);
    this.mouseEnterHandler = this.handleMouseEnter.bind(this);
    this.mouseLeaveHandler = this.handleMouseLeave.bind(this);

    this.elements.container.addEventListener(
      'mousemove',
      this.mouseMoveHandler
    );
    this.elements.container.addEventListener(
      'mouseenter',
      this.mouseEnterHandler
    );
    this.elements.container.addEventListener(
      'mouseleave',
      this.mouseLeaveHandler
    );
  }

  removeMouseEvents() {
    if (this.mouseMoveHandler) {
      this.elements.container.removeEventListener(
        'mousemove',
        this.mouseMoveHandler
      );
      this.elements.container.removeEventListener(
        'mouseenter',
        this.mouseEnterHandler
      );
      this.elements.container.removeEventListener(
        'mouseleave',
        this.mouseLeaveHandler
      );

      this.mouseMoveHandler = null;
      this.mouseEnterHandler = null;
      this.mouseLeaveHandler = null;
    }
  }

  handleMouseMove(e) {
    const rect = this.elements.container.getBoundingClientRect();
    this.state.mouseX = e.clientX - rect.left;
    this.state.mouseY = e.clientY - rect.top;

    if (this.state.isMouseInside) {
      this.updateLabelPosition(this.state.mouseX, this.state.mouseY);

      if (!this.state.isRevealed) {
        this.updateClipPath(
          this.CLIP_RADIUS,
          this.state.mouseX,
          this.state.mouseY
        );
      }
    }
  }

  handleMouseEnter(e) {
    this.state.isMouseInside = true;

    const rect = this.elements.container.getBoundingClientRect();
    this.state.mouseX = e.clientX - rect.left;
    this.state.mouseY = e.clientY - rect.top;

    if (!this.state.isRevealed) {
      this.showCursor();
    }
  }

  handleMouseLeave() {
    this.state.isMouseInside = false;

    if (!this.state.isRevealed) {
      this.hideCursor();
    }
  }

  handleClick(e) {
    e.preventDefault();
    this.toggleVideo();
  }

  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      // Re-evaluate the component state based on new viewport/pointer capabilities
      this.reinitializeForBreakpoint();
      this.moveSearchForm();
    }, 250);
  }

  reinitializeForBreakpoint() {
    // Update mouse event listeners based on current viewport
    this.updateMouseEvents();

    if (this.state.isRevealed) {
      // Video is currently playing - update close button display
      if (!this.isMobile() && this.hasFinePointer()) {
        // Desktop with cursor - show cursor-following close text
        this.elements.label.innerHTML =
          '<span class="block text-sm font-medium tracking-wider">CLOSE</span>';
        this.elements.label.style.opacity = '1';
        this.hideMobileCloseButton();
      } else {
        // Mobile or touch device - show mobile close button, hide text
        this.elements.label.style.opacity = '0';
        this.showMobileCloseButton();
      }

      // Handle overlay visibility based on mobile breakpoint
      if (!this.isMobile() && this.elements.heroOverlay) {
        this.elements.heroOverlay.style.opacity = '0';
      } else if (this.isMobile() && this.elements.heroOverlay) {
        this.elements.heroOverlay.style.opacity = '1';
      }
    } else {
      // Video is not playing - update play button display
      if (this.shouldShowCursor()) {
        // Desktop with cursor - handle cursor tracking
        this.state.isMouseInside = false; // Reset mouse state
        this.updateClipPath(0, 0, 0);
        this.elements.label.style.opacity = '0';
        this.checkInitialMousePosition(); // Re-check mouse position
      } else {
        // Mobile/touch - center the play button
        const center = this.getCenterPosition();
        this.updateClipPath(this.CLIP_RADIUS, center.x, center.y);
        this.updateLabelPosition(center.x, center.y);
        this.elements.label.style.opacity = '1';
      }

      // Ensure overlay is visible when not playing
      if (this.elements.heroOverlay) {
        this.elements.heroOverlay.style.opacity = '1';
      }

      // Always hide mobile close button when not playing
      this.hideMobileCloseButton();
    }
  }

  toggleVideo() {
    this.state.isRevealed = !this.state.isRevealed;

    if (this.state.isRevealed) {

      if (window.plausible) {
        window.plausible('Video Hero - Opened');
      }

      this.elements.clipBtn.style.transition =
        'clip-path 0.6s cubic-bezier(0.77, 0, 0.18, 1)';
      this.elements.clipBtn.style.clipPath = 'circle(150% at 50% 50%)';

      // Only show close text on desktop with fine pointer support
      if (!this.isMobile() && this.hasFinePointer()) {
        this.elements.label.innerHTML =
          '<span class="block text-sm font-medium tracking-wider">CLOSE</span>';
      } else {
        this.elements.label.style.opacity = '0';
      }

      // Enable sound but keep controls disabled for better mobile compatibility
      this.elements.maskVideo.muted = false;
      this.elements.maskVideo.controls = false;

      // Ensure video is playing
      if (this.elements.maskVideo.paused) {
        this.elements.maskVideo.play().catch(() => {
          console.log('Video play failed');
        });
      }

      // Fade out overlay content after mobile breakpoint
      if (!this.isMobile() && this.elements.heroOverlay) {
        this.elements.heroOverlay.style.transition = 'opacity 0.3s ease-out';
        this.elements.heroOverlay.style.opacity = '0';
      }

      // Show mobile close button for mobile screens or devices without fine pointer
      if (this.isMobile() || !this.hasFinePointer()) {
        this.showMobileCloseButton();
      }
    } else {
      this.elements.clipBtn.style.transition =
        'clip-path 0.4s cubic-bezier(0.65, 0, 0.35, 1)';

      this.elements.label.innerHTML =
        '<span class="block text-sm font-medium tracking-wider">PLAY</span>';

      // Mute and ensure no controls
      this.elements.maskVideo.muted = true;
      this.elements.maskVideo.controls = false;

      if (this.elements.heroOverlay) {
        setTimeout(() => {
          this.elements.heroOverlay.style.transition = 'opacity 0.3s ease-in';
          this.elements.heroOverlay.style.opacity = '1';
        }, 200);
      }

      this.hideMobileCloseButton();

      if (this.isMobile()) {
        const center = this.getCenterPosition();
        this.updateClipPath(this.CLIP_RADIUS, center.x, center.y);
        this.updateLabelPosition(center.x, center.y);
        this.elements.label.style.opacity = '1';
      } else {
        if (this.state.isMouseInside) {
          this.updateClipPath(
            this.CLIP_RADIUS,
            this.state.mouseX,
            this.state.mouseY
          );
          this.elements.label.style.opacity = '1';
        } else {
          this.updateClipPath(0, this.state.mouseX, this.state.mouseY);
          this.elements.label.style.opacity = '0';
        }

        // Reset transitions after animation
        setTimeout(() => {
          this.elements.clipBtn.style.transition = 'none';
        }, 450);
      }
    }
  }

  showMobileCloseButton() {
    if (document.getElementById('mobile-video-close')) return;

    const closeBtn = document.createElement('button');
    closeBtn.id = 'mobile-video-close';
    closeBtn.className =
      'absolute top-4 right-4 z-50 bg-black bg-opacity-50 rounded-full p-2 text-white';
    closeBtn.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="31" height="2" transform="matrix(0.706504 -0.707709 0.706504 0.707709 0.413086 23)" fill="currentColor"/>
        <rect width="31" height="2" transform="matrix(0.706504 0.707709 -0.706504 0.707709 1.41309 0)" fill="currentColor"/>
      </svg>
    `;

    closeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggleVideo();
    });

    this.elements.container.appendChild(closeBtn);
  }

  hideMobileCloseButton() {
    const closeBtn = document.getElementById('mobile-video-close');
    if (closeBtn) {
      closeBtn.remove();
    }
  }

  moveSearchForm() {
    const formWrapper =
      document.querySelector('.js-mobile-search-container > article') ||
      document.querySelector('.js-search-form-wrapper > article');
    const desktopWrapper = document.querySelector('.js-search-form-wrapper');
    const mobileContainer = document.querySelector(
      '.js-mobile-search-container'
    );

    if (!formWrapper || !desktopWrapper || !mobileContainer) {
      return;
    }

    if (this.isMobile()) {
      if (!mobileContainer.contains(formWrapper)) {
        mobileContainer.appendChild(formWrapper);
      }
    } else {
      if (!desktopWrapper.contains(formWrapper)) {
        desktopWrapper.appendChild(formWrapper);
      }
    }
  }
}

// Ensure DOM is ready before initializing
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new VideoHero();
  });
} else {
  new VideoHero();
}
